/**
 * Pipeline Context Implementation
 *
 * Mutable context passed to each hook during pipeline execution.
 * Hooks read metadata and envelope, but can mutate body.
 */

import type { IEnvelope, IPipelineContext, IPipelinePause, IUserInputRequest } from '../types';

/**
 * Mutable context for pipeline execution
 * Passed to each hook, allowing transformation of message body
 */
export class PipelineContext<TBody = unknown> implements IPipelineContext<TBody> {
  readonly envelope: IEnvelope<TBody>;

  readonly serverId: string;

  body: TBody;

  metadata: Record<string, unknown> = {};

  private _aborted = false;

  private _paused = false;

  private pauseResolve?: (value: unknown) => void;

  private pauseReject?: (reason?: Error) => void;

  private pausePromise?: Promise<unknown>;

  private _activityLog: string[] = [];

  constructor(envelope: IEnvelope<TBody>, serverId: string) {
    this.envelope = envelope;
    this.serverId = serverId;
    this.body = envelope.body;
  }

  abort(reason?: string): void {
    this._aborted = true;
    if (reason) {
      this.metadata.abortReason = reason;
    }
  }

  get isAborted(): boolean {
    return this._aborted;
  }

  pause(request: IUserInputRequest): IPipelinePause {
    this._paused = true;
    this.metadata.pauseRequest = request;

    // Create promise for pause/resume
    const pausePromise = new Promise<unknown>((resolve, reject) => {
      this.pauseResolve = resolve;
      this.pauseReject = reject;
    });

    this.pausePromise = pausePromise;

    return {
      requestId: request.id,
      pausedAt: new Date(),
      resume: async (response: unknown) => {
        this.metadata.pauseResponse = response;
        this._paused = false;
        if (this.pauseResolve) {
          this.pauseResolve(response);
        }
      },
      reject: async (error: Error) => {
        this._paused = false;
        if (this.pauseReject) {
          this.pauseReject(error);
        }
      },
    };
  }

  get isPaused(): boolean {
    return this._paused;
  }

  /**
   * Wait for pause to be resolved or rejected
   * (Internal use only)
   */
  async waitForPauseResolution(): Promise<unknown> {
    if (this.pausePromise) {
      return this.pausePromise;
    }
    return undefined;
  }

  /**
   * Get the current activity log (read-only)
   */
  get activityLog(): readonly string[] {
    return this._activityLog;
  }

  /**
   * Add an activity to the log.
   * Hooks use this to report actions taken during execution.
   *
   * @param message Activity description
   */
  addActivity(message: string): void {
    this._activityLog.push(message);
  }

  /**
   * Clear the activity log (for next hook).
   * Internal use only.
   */
  clearActivityLog(): void {
    this._activityLog = [];
  }
}
